import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Toast, { useToast } from '../components/Toast';
import { expensesAPI, api } from '../services/api';
import { Upload, FileText, X, Paperclip, Loader2, ImageIcon } from 'lucide-react';
import { BackHeader } from '../components/BackHeader';
import { resolveUploadUrl } from '../services/uploads';

interface Expense {
  id: string;
  title?: string;
  description: string;
  amount: number;
  category: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedAt?: string;
  receipt_url?: string;
  receiptUrl?: string; // Bug #159: API returns camelCase receiptUrl; accept both cases
}

const expenseCategories = [
  'Mantenimiento General',
  'Jardineria',
  'Seguridad',
  'Limpieza',
  'Reparaciones',
  'Servicios',
  'Administracion',
  'Otros'
];

export default function Expenses() {
  const { user, isAdmin } = useAuth();
  const { toasts, removeToast, success, error } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [newExpense, setNewExpense] = useState({
    category: '',
    description: '',
    amount: ''
  });
  // F29: Categorías de egresos del condominio (incluye categorías personalizadas)
  const [apiCategories, setApiCategories] = useState<string[]>([]);
  // F30: Receipt upload state — inline in the create modal
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptExpense, setReceiptExpense] = useState<Expense | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadExpenses();
    loadCategories();
  }, []);

  // F29: Load the condo's expense categories (including custom ones created in /categorias)
  const loadCategories = async () => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const condoId = currentUser.condoId;
    if (!condoId) return;
    try {
      const res = await api.get(`/financial/condo/${condoId}/categories`, { params: { type: 'expense' } });
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : data?.categories ?? data?.items ?? [];
      const names = items
        .map((c: any) => (typeof c === 'string' ? c : c?.name))
        .filter((n: unknown): n is string => typeof n === 'string' && n.length > 0);
      setApiCategories(names);
    } catch (err) {
      // Fallback silencioso: se mantienen las categorías predeterminadas
      console.error('Error loading expense categories:', err);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = currentUser.condoId;
      if (!condoId) {
        setExpenses([]);
        return;
      }
      const response = await expensesAPI.getAll(condoId);
      const data = response.data;
      const list = data?.data?.expenses ?? data?.expenses ?? data ?? [];
      setExpenses(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading expenses:', err);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // ─── F30: Handle file selection in the create form ───────────────────────
  const handleFileSelect = (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      error('Formato no válido. Use PDF, JPG, PNG o WebP.', undefined, { report: false });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      error('El archivo excede 5MB.', undefined, { report: false });
      return;
    }
    setReceiptFile(file);
    if (file.type !== 'application/pdf') {
      const reader = new FileReader();
      reader.onload = () => setReceiptPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── F30: Upload receipt after expense creation ──────────────────────────
  // BUG #147: single direct call with the backend JSON contract
  // ({ file: base64 }) and a user-visible error toast — the previous
  // multipart-then-fallback flow 400'd server-side and hung silently.
  const uploadReceiptForExpense = async (expenseId: string): Promise<void> => {
    if (!receiptFile) return;
    setUploadingReceipt(true);
    try {
      await expensesAPI.uploadReceipt(expenseId, receiptFile);
      success('Recibo subido exitosamente');
    } catch (err: any) {
      console.error('Receipt upload failed:', err);
      error(
        err?.response?.data?.error ||
          'No se pudo subir el recibo. Puede intentarlo más tarde desde la tabla.'
      );
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitBtn = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) submitBtn.disabled = true;

    try {
      // F27: descripción mínima de 10 caracteres (validación en cliente, antes de llamar a la API)
      if ((newExpense.description || '').trim().length < 10) {
        error('La descripción debe tener al menos 10 caracteres');
        if (submitBtn) submitBtn.disabled = false;
        return;
      }
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = currentUser.condoId;
      if (!condoId) {
        error('No se pudo determinar el condominio');
        return;
      }
      // Create the expense — POST returns the new expense with its ID
      const response = await expensesAPI.create(condoId, {
        description: newExpense.description,
        amount: parseFloat(newExpense.amount),
        category: newExpense.category
      });

      // Extract expenseId from response
      const respData = response?.data?.data ?? response?.data ?? response;
      const expenseId = respData?.id ?? respData?.expense?.id ?? respData?.expenseId;

      // F30: If a receipt file was selected, upload it now
      if (receiptFile && expenseId) {
        await uploadReceiptForExpense(expenseId);
      } else if (!receiptFile) {
        success('Gasto registrado exitosamente');
      } else {
        // R1-A2: receipt was selected but the create response had no usable id — the receipt would be silently dropped
        error('El gasto fue registrado, pero el comprobante no pudo adjuntarse. Edítalo y súbelo de nuevo.');
      }

      // Reset form
      setShowModal(false);
      setNewExpense({ category: '', description: '', amount: '' });
      clearReceipt();
      loadExpenses();
    } catch (err) {
      console.error('Error creating expense:', err);
      error('Error al registrar gasto');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  };

  const filteredExpenses = filterCategory === 'all'
    ? expenses
    : expenses.filter(e => e.category === filterCategory);
  // Bug #159: normalize receipt path — API returns camelCase receiptUrl (older rows snake_case receipt_url)
  const receiptPathFor = (e: Expense) => e.receiptUrl || e.receipt_url;
  // F29: Combinar categorías del condominio + predeterminadas + valores existentes (compatibilidad texto libre)
  // R1-A1: dedupe case/accent-insensitively (trim + locale lowercase + strip diacritics), keep the first display string
  const categorySeen = new Set<string>();
  const categoryOptions: string[] = [];
  for (const cat of [
    ...expenseCategories,
    ...apiCategories,
    ...expenses.map(e => e.category).filter(Boolean),
  ]) {
    const key = cat.trim().toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (!categorySeen.has(key)) {
      categorySeen.add(key);
      categoryOptions.push(cat.trim());
    }
  }
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  // ─── Guard: no condo ─────────────────────────────────────────────────────
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const condoId = currentUser.condoId;

  if (!condoId && !loading) return (
    <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-12 text-center">
      <div className="text-6xl mb-4">🏘️</div>
      <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">Sin condominio asignado</h3>
      <p className="text-[var(--v-text-secondary)]">No se ha asignado un condominio a tu cuenta.</p>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)]">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-[var(--v-text-secondary)] mt-4">Cargando gastos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Gastos y Egresos</h2>
          <p className="text-[var(--v-text-secondary)]">Administracion economica del condominio</p>
        </div>
        {isAdmin() && (
          <button onClick={() => setShowModal(true)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg">+ Registrar Gasto</button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Total Gastos</p>
          <p className="text-3xl font-bold text-red-400 mt-2">${totalExpenses.toLocaleString()}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Transacciones</p>
          <p className="text-3xl font-bold text-orange-400 mt-2">{filteredExpenses.length}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="text-[var(--v-text-secondary)]">Filtrar:</label>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white">
          <option value="all">Todas las categorias</option>
          {categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>

      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        {filteredExpenses.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-6xl mb-4">💸</div>
            <p className="text-[var(--v-text-secondary)]">No hay gastos registrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="text-left p-4 text-[var(--v-text-secondary)]">Categoria</th>
                  <th className="text-left p-4 text-[var(--v-text-secondary)]">Descripcion</th>
                  <th className="text-right p-4 text-[var(--v-text-secondary)]">Monto</th>
                  <th className="text-left p-4 text-[var(--v-text-secondary)]">Estado</th>
                  {isAdmin() && <th className="text-left p-4 text-[var(--v-text-secondary)]">Recibo</th>}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-[var(--v-surface-3)]/50">
                    <td className="p-4"><span className="bg-slate-700 text-slate-300 px-2 py-1 rounded text-sm">{expense.category}</span></td>
                    <td className="p-4 text-[var(--v-text-secondary)]">{expense.description}</td>
                    <td className="p-4 text-right text-red-400 font-medium">${(expense.amount || 0).toLocaleString()}</td>
                    <td className="p-4">
                      {expense.status === 'approved' || expense.approvedBy ? (
                        <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-sm">Aprobado</span>
                      ) : expense.status === 'rejected' ? (
                        <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-sm">Rechazado</span>
                      ) : (
                        <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-sm">Pendiente</span>
                      )}
                    </td>
                    {isAdmin() && (
                      <td className="p-4">
                        {receiptPathFor(expense) ? (
                          <button
                            type="button"
                            onClick={() => { resolveUploadUrl(receiptPathFor(expense)!).then((u) => window.open(u, '_blank', 'noopener')).catch(() => {}); }}
                            className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                          >
                            <Paperclip className="w-4 h-4" /> Ver
                          </button>
                        ) : (
                          <button
                            onClick={() => setReceiptExpense(expense)}
                            className="inline-flex items-center gap-1 text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] text-sm"
                          >
                            <Upload className="w-4 h-4" /> Subir
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-[var(--v-text-primary)] mb-4">Registrar Nuevo Gasto</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Categoria *</label>
                <select value={newExpense.category} onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white" required>
                  <option value="">Seleccionar...</option>
                  {categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Descripcion *</label>
                <textarea value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} rows={3} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white" required />
              </div>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Monto *</label>
                <input type="number" step="0.01" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white" required />
              </div>

              {/* F30: Inline receipt upload in the create form */}
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-2">Recibo / Comprobante</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelect(file); }}
                />
                {!receiptFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={handleDrop}
                    className="border-2 border-dashed border-slate-600 hover:border-slate-500 rounded-lg p-6 text-center cursor-pointer transition-colors"
                  >
                    <Upload className="w-7 h-7 text-slate-500 mx-auto mb-2" />
                    <p className="text-[var(--v-text-secondary)] text-sm">Arrastra un archivo o haz clic para seleccionar</p>
                    <p className="text-slate-500 text-xs mt-1">PDF, JPG, PNG, WebP · Máximo 5MB</p>
                  </div>
                ) : (
                  <div className="border border-slate-600 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[var(--v-text-secondary)] min-w-0">
                        {receiptFile.type === 'application/pdf' ? (
                          <FileText className="w-5 h-5 text-red-400 flex-shrink-0" />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        )}
                        <span className="text-sm truncate">{receiptFile.name}</span>
                      </div>
                      <button type="button" onClick={clearReceipt} className="text-[var(--v-text-secondary)] hover:text-red-400 flex-shrink-0 ml-2">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Image preview */}
                    {receiptPreview && (
                      <div className="rounded-lg overflow-hidden border border-[var(--v-surface-3)]">
                        <img src={receiptPreview} alt="Vista previa" className="w-full max-h-40 object-contain bg-[var(--v-surface-1)]" />
                      </div>
                    )}
                    {/* PDF indicator */}
                    {receiptFile.type === 'application/pdf' && (
                      <div className="flex items-center gap-2 text-[var(--v-text-secondary)] text-xs">
                        <FileText className="w-4 h-4" />
                        Archivo PDF seleccionado ({(receiptFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingReceipt ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo recibo...</>
                  ) : (
                    'Registrar Gasto'
                  )}
                </button>
                <button type="button" onClick={() => { setShowModal(false); clearReceipt(); }} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* F30: Receipt Upload Modal (for existing expenses) */}
      {receiptExpense && (
        <ReceiptUploadModal
          expense={receiptExpense}
          onClose={() => setReceiptExpense(null)}
          onUploaded={() => {
            setReceiptExpense(null);
            loadExpenses();
          }}
        />
      )}

      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

// ─── F30: Receipt Upload Modal (for existing expenses from the table) ───────
function ReceiptUploadModal({ expense, onClose, onUploaded }: {
  expense: Expense;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { toasts, removeToast, success, error } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((f: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(f.type)) {
      error('Formato no válido. Use PDF, JPG, PNG o WebP.', undefined, { report: false });
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      error('El archivo excede 5MB.', undefined, { report: false });
      return;
    }
    setFile(f);
    if (f.type !== 'application/pdf') {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  }, [error]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // BUG #147: single direct call with the backend JSON contract; every
  // failure path now surfaces an error toast (the old base64 fallback threw
  // inside an async reader.onload handler -> silent unhandled rejection).
  const handleUpload = async () => {
    if (!file) { error('Selecciona un archivo primero'); return; }
    setUploading(true);
    try {
      await expensesAPI.uploadReceipt(expense.id, file);
      success('Recibo subido exitosamente');
      onUploaded();
    } catch (err: any) {
      console.error('Receipt upload failed:', err);
      error(err?.response?.data?.error || 'Error al subir el recibo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <BackHeader fallback="/" />
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Subir Recibo</h3>
          <button onClick={onClose} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[var(--v-text-secondary)] text-sm mb-4">Gasto: {expense.description}</p>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[#36CCD8] bg-[#36CCD8]/5' : 'border-slate-600 hover:border-slate-500'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-[var(--v-text-secondary)]">
              <FileText className="w-5 h-5" />
              <span className="text-sm">{file.name}</span>
            </div>
          ) : (
            <div>
              <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-[var(--v-text-secondary)] text-sm">Arrastra el archivo aquí o haz clic para seleccionar</p>
              <p className="text-slate-500 text-xs mt-1">PDF, JPG, PNG, WebP · Máximo 5MB</p>
            </div>
          )}
        </div>

        {/* Preview */}
        {preview && (
          <div className="mt-4 rounded-lg overflow-hidden border border-[var(--v-surface-3)]">
            <img src={preview} alt="Vista previa" className="w-full max-h-48 object-contain bg-[var(--v-surface-1)]" />
          </div>
        )}
        {file && file.type === 'application/pdf' && (
          <div className="mt-4 flex items-center gap-2 text-[var(--v-text-secondary)] text-sm">
            <FileText className="w-4 h-4" />
            Archivo PDF seleccionado ({(file.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-[#36CCD8] hover:bg-[#2bb9c5] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo...</> : <><Upload className="w-4 h-4" /> Subir Recibo</>}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
      {/* F29-R1 fix: local Toast host so this modal's own useToast() toasts render
          (fix marker: receipt-upload-modal-toast-host) */}
      <div data-testid="receipt-upload-modal-toast-host">
        <Toast toasts={toasts} onRemove={removeToast} />
      </div>
    </div>
  );
}
